'use client';

import { Stack, Typography } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { useMemo, useState} from "react";
import { formatOpeningHoursShortText } from "@/app/features/sites/opening-hours/helper/text-presenter";
import { MutableGeneralOpeningHour } from "@/app/features/sites/opening-hours/model/types";

interface OpeningHoursViewProps {
    generalOpeningHours: MutableGeneralOpeningHour[];
    variant?: 'inline' | 'default';
}

interface VariantProps {
    text?: string;
}

export default function GeneralOpeningHoursView({ generalOpeningHours, variant = 'default' }: OpeningHoursViewProps) {

    const openingHoursText = useMemo(() => {
        return formatOpeningHoursShortText(generalOpeningHours);
    }, [generalOpeningHours])

    switch (variant) {
        case 'inline':
            return <InlineVariant text={openingHoursText} />;
        default:
            return <DefaultVariant text={openingHoursText} />;
    }
}

function DefaultVariant({ text }: VariantProps) {
    return (
        <Typography
            variant="h4"
            sx={{
                textAlign: 'center',
                fontWeight: 800,
            }}
            component="div"
        >
            {text}
        </Typography>
    )
}

function InlineVariant({ text }: VariantProps) {
    const [isHovering, setIsHovering] = useState<boolean>(false);

    return (
        <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            sx={{
                transformOrigin: 'center',
                willChange: 'transform',
                overflow: 'visible',
            }}
        >
            <AccessTime
                sx={{
                    color: (theme) => isHovering ? theme.palette.primary.main : theme.palette.secondary.dark,
                    fontSize: 'calc(var(--current-font-size, var(--font-size-medium)) * 1.25)',
                    scale: isHovering ? 1.5 : 1,
                    transition: 'color .3s ease, scale .3s ease',
                }}
            />
            <Typography
                variant="body2"
                sx={{
                    color: (theme) => isHovering ? theme.palette.secondary.main : theme.palette.secondary.dark,
                    fontSize: `calc(var(--current-font-size, var(--font-size-medium)))`,
                    textTransform: 'uppercase',
                    transition: 'color .3s ease, font-size .3s ease',
                    lineHeight: 1
                }}
            >
                <span
                    style={{
                        transform: `scale(${isHovering ? 1 : .9})`,
                        transformOrigin: 'left center',
                        transition: 'transform .3s ease, color .3s ease',
                        display: 'inline-block',
                        willChange: 'transform',
                    }}
                >
                    {text}
                </span>
            </Typography>
        </Stack>
    )
}